import { createRouter, createWebHistory } from 'vue-router';

import { useAuthStore } from '@/stores/AuthStore.ts';

import { cleanStorage, useAuth } from '@/composables/Auth.ts';
import { captureError } from '@/composables/Sentry.ts';
import { getStoredUser } from '@/composables/SessionStorage.ts';
import { useOrganizationsStore } from '@/stores/v2/OrganizationStore.ts';

import type { OrganizationUser } from '@/clients/v1';

import { UNRESOLVED_ROUTE_COMPONENT_ERROR } from '@/config/errorHandling.ts';

import { adminRoutes } from '@/router/modules/adminRoutes.ts';
import { authRoutes } from '@/router/modules/authRoutes.ts';
import { connectorRoutes } from '@/router/modules/connectorRoutes.ts';
import { dashboardRoutes } from '@/router/modules/dashboardRoutes.ts';
import { errorRoutes } from '@/router/modules/errorRoutes.ts';
import { notificationRoutes } from '@/router/modules/notificationRoutes.ts';
import { organizationRoutes } from '@/router/modules/organizationRoutes.ts';
import { policiesRoutes } from '@/router/modules/policiesRoutes.ts';
import { projectRoutes } from '@/router/modules/projectRoutes.ts';
import { vendorRoutes } from '@/router/modules/vendorRoutes.ts';
import { settingsRoutes } from './modules/settingsRoutes';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    ...authRoutes,
    ...adminRoutes,
    ...connectorRoutes,
    ...dashboardRoutes,
    ...errorRoutes,
    ...notificationRoutes,
    ...organizationRoutes,
    ...policiesRoutes,
    ...projectRoutes,
    ...vendorRoutes,
    ...settingsRoutes,
    {
      path: '/logout-azure',
      name: 'logout-azure',
      redirect: { name: 'login-page' },
      beforeEnter: async (to, from, next) => {
        cleanStorage();
        next();
      },
    },
    {
      path: '/:catchAll(.*)',
      redirect: () => {
        window.location.href = '/not-found';
        return '/not-found'; // fake redirect
      },
    },
  ],
});

router.beforeEach(async (to) => {
  const authStore = useAuthStore();
  const organizationsStore = useOrganizationsStore();
  const { logout } = useAuth();

  let parsedUser: OrganizationUser | null = null;

  try {
    parsedUser = getStoredUser();
  } catch (error) {
    captureError(error, {
      message: 'Component: Router, File: index.ts, Error: parsing user from localStorage',
    });
  }

  const publicPages = [
    'login-page',
    'oauth2-redirect',
    'connector-oauth-callback',
    'no-organization',
    'problem',
    'not-found',
    // Reached when the backend's signup flow caught a transient IdP outage —
    // the user is not yet authenticated, so the page must be allow-listed.
    'signup-pending',
  ];

  const toName = to.name ? to.name.toString() : null;
  const isPublicPage = toName ? publicPages.includes(toName) : false;

  document.title = 'Modulos';

  // If a returning user lands on /login with a still-valid session, send them straight
  // into the app instead of showing the Login button. A dead/expired session falls
  // through to the manual button below, so there is no redirect loop.
  if (toName === 'login-page' && parsedUser) {
    const freshToken = await authStore.getAccessTokenFromRefreshToken();
    if (freshToken) {
      return { name: 'home' };
    }
  }

  // 0. No user — nothing to log out from. Going through logout() would
  // route to the IdP's end_session and strand the user on its empty-state page.
  // cleanStorage() handles any partial local state (stray tokens, stale stores).
  if (!parsedUser && !isPublicPage) {
    cleanStorage();
    return { name: 'login-page' };
  }

  // 1. User must accept the terms but hasn't (computed by the backend on /users/me;
  // false for users of custom-terms organizations, who never see the platform terms)
  if (parsedUser && parsedUser.terms_acceptance_required && !isPublicPage) {
    logout();
    return false;
  }

  // 2. Ensure a fresh access token
  if (!isPublicPage) {
    const freshToken = await authStore.getAccessTokenFromRefreshToken();
    if (!freshToken) {
      logout();
      return false;
    }
  }

  // 3. No organization
  if (parsedUser && !parsedUser.organization && toName !== 'no-organization') {
    return { name: 'no-organization' };
  }

  // 4. Ensure user and config
  if (!isPublicPage && (!authStore.user || !organizationsStore.configData)) {
    try {
      await authStore.getMeAndOrgConfig();
    } catch (error) {
      captureError(error, {
        message: 'Component: Router, File: index.ts, Error: getMeAndOrgConfig',
      });
      logout();
      return false;
    }
  }

  return true;
});

// A registered handler replaces vue-router's own console.error, so keep it for every other error
router.onError((error: unknown) => {
  if (error instanceof Error && UNRESOLVED_ROUTE_COMPONENT_ERROR.test(error.message)) return;
  console.error(error);
});

export default router;
